(ns hai.web-app.add-menu
  "Task add modal.

  Mobile drives an add bottom-sheet (hai.home.mobile.add-sheet) whose task
  tile opens the modal defined here, as does the desktop universal
  \"Add anything…\" bar; note creation goes through the expanded editor
  (hai.notes.create) on every surface."
  (:require
   [hai.tasks.assignee-picker :as assignee-picker]
   [hai.tasks.data :as tasks-data]
   [hai.tasks.form :as form]
   [hai.tasks.header :as header]
   [hai.tasks.mini-app :as tasks-mini-app]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e defevent defevent-fx defsub]]
   [lambdaisland.ornament :as o]))

;; ---------------------------------------------------------------------------
;; Task events
;;
;; Task creation reuses hai.tasks.data/create, which persists via the
;; task/create resolver and prepends to :hai.tasks.data/list — the same
;; key the Desktop card subscribes to and reloads on every visit. So a task
;; added from here shows up wherever tasks are shown, no extra refresh.
;; ---------------------------------------------------------------------------

(defsub ::task-modal-open? [db]
  (get db ::task-modal-open? false))

(defsub ::task-modal-title [db]
  (get db ::task-modal-title))

;; `title` seeds the composer for a host that already asked for one — the
;; universal bar hands over whatever was typed into it.
(defevent ::open-task-modal [db title]
  (assoc db ::task-modal-open? true ::task-modal-title title))

(defevent ::close-task-modal [db _]
  (dissoc db ::task-modal-open? ::task-modal-title))

;; ---------------------------------------------------------------------------
;; Task modal
;; ---------------------------------------------------------------------------

;; Radix wires the dialog's accessible name and description to its own Title
;; and Description; the visible title is the card's header bar, so both are
;; hidden here rather than rendered twice.
(def ^:private visually-hidden
  {:position "absolute" :width "1px" :height "1px" :padding "0" :margin "-1px"
   :overflow "hidden" :clip "rect(0 0 0 0)" :white-space "nowrap" :border "0"})

;; The mini-app card is the modal's surface, so the dialog panel underneath it
;; keeps only its size — no padding and no background of its own.
(def ^:private bare-dialog-panel
  {:padding "0" :background "transparent" :box-shadow "none"})

;; The duck-tape strip pins a card to the dashboard. A modal floats over the
;; page instead, so it sheds the tape and keeps the rest of the card.
(o/defstyled modal-card tasks-mini-app/card
  [:&::before {:display "none"}])

(def ^:private close-icon (icons/mui-icon :outlined :close))

(defn task-modal []
  (let [open? @(<s [::task-modal-open?])
        title @(<s [::task-modal-title])
        close! (fn [] (>e [::close-task-modal]))]
    [c/DialogRoot
     {:open open?
      :onOpenChange (fn [opening?]
                      (when-not opening?
                        (close!)))}
     [c/DialogContent {:max-width "480px" :style bare-dialog-panel}
      [c/DialogTitle {:style visually-hidden} "Add a task"]
      [c/DialogDescription {:style visually-hidden} "Create a new task."]
      ;; Mounted only while open, so each visit gets a fresh composer — the
      ;; form stages into its own atoms and has nothing to reset. No assignee is
      ;; staged: an unpicked composer means the creator, same as everywhere.
      (when open?
        [modal-card
         [header/bar {:title "Add a task"}
          [:button.bar-action-btn
           {:type "button"
            :aria-label "Cancel"
            :title "Cancel"
            :on-click close!}
           [close-icon {:size 18}]]]
         [form/task-form
          {:show-hint? true
           :initial (when (seq title) {:title title})
           :on-save (fn [task] (>e [::tasks-data/create task]))
           :on-close close!
           :render-assignee-picker
           (fn [{:keys [on-pick trigger]}]
             [assignee-picker/component
              {:editable? true
               :on-pick on-pick
               :label "Assign"
               :trigger trigger}])}]])]]))

(defn modals []
  [:<>
   [task-modal]])
